import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, MessageCircle, Star, Check, X, Sparkles, Ellipsis } from 'lucide-react';
import { database } from '../supabase';
// import { fetchQuizData } from '../firebase-setup'; // Backup - keeping dormant
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, handleRedirectResult } from '../utils/authUtils';
import { triggerQuizAnalyticsUpdate } from '../utils/analytics';
import './TakeQuiz.css';

const TakeQuiz = () => {
    const { uid, quizId } = useParams();
    const navigate = useNavigate();
    const [showTip, setShowTip] = useState(false);

    const handleShowTip = () => {
        setShowTip(!showTip);
    }

    // Close tooltip when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showTip && !event.target.closest('.take-quiz-tip') && !event.target.closest('.take-quiz-ellipsis-btn')) {
                setShowTip(false);
                setShareClicked(false); // Reset share state when closing
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showTip]);


    const [shareClicked, setShareClicked] = useState(false);

    const handleShareClicked = () => {
        setShareClicked(!shareClicked);
    }



  

    const handleShareQuiz = (shareUrl) => {
        console.log('Sharing quiz');
        navigator.clipboard.writeText(shareUrl);
        
        // Show "Copied!" feedback
        setShareClicked(true);
        
        // Reset after 2 seconds
        setTimeout(() => {
            setShareClicked(false);
            setShowTip(false);
        }, 2000);
    }

    const handleDeleteQuiz = () => {
        console.log('Deleting quiz');
        setShowTip(false);
    }
    
    // Quiz state
    const [quizData, setQuizData] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showExplanations, setShowExplanations] = useState({});
    const [answerFeedback, setAnswerFeedback] = useState({});
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quizStartTime, setQuizStartTime] = useState(null);
    const [attemptSaved, setAttemptSaved] = useState(false);
    const [shuffledQuestions, setShuffledQuestions] = useState([]);
    
    // User state
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const { user, authInitialized } = useAuth();

    // Helper function to clean text and remove asterisks
    const cleanText = (text) => {
        if (!text) return '';
        return text.replace(/\*+/g, '').trim();
    };

    // Helper function to shuffle array
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };



    // Helper function to process and shuffle quiz questions
    const processQuizData = (data) => {
        // Handle both Firebase and Supabase data structures
        const questions = data.questions || data.quiz_data;
        if (!data || !questions) return data;

        const processedQuestions = questions.map(question => {
            // Clean question text
            const cleanQuestion = cleanText(question.question);
            
            // Clean and shuffle options
            const cleanOptions = question.options.map(option => cleanText(option));
            const shuffledOptions = shuffleArray(cleanOptions);
            
            // Find the correct answer index after shuffling
            const originalCorrectAnswer = cleanText(question.options[question.correct_answer || 0]);
            const newCorrectIndex = shuffledOptions.findIndex(option => option === originalCorrectAnswer);
            
            return {
                ...question,
                question: cleanQuestion,
                options: shuffledOptions,
                correct_answer: newCorrectIndex >= 0 ? newCorrectIndex : 0,
                original_correct_answer: originalCorrectAnswer
            };
        });

        return {
            ...data,
            questions: processedQuestions
        };
    };

    useEffect(() => {
        const loadQuiz = async () => {
            try {
                setLoading(true);
                console.log('🔍 Loading quiz with Firebase UID:', uid, 'Quiz ID:', quizId);
                
                // Use Supabase to fetch quiz data
                const data = await database.getQuizByFirebaseUid(uid, quizId);
                console.log('✅ Quiz data loaded from Supabase:', data);
                
                const processedData = processQuizData(data);
                setQuizData(processedData);
                setShuffledQuestions(processedData.questions);
                // Set quiz start time for tracking
                setQuizStartTime(Date.now());
            } catch (err) {
                console.error('❌ Error loading quiz from Supabase:', err);
                setError('Failed to load quiz. Please check the URL and try again.');
            } finally {
                setLoading(false);
            }
        };

        loadQuiz();
    }, [uid, quizId]);

    // Handle redirect result on page load
    useEffect(() => {
        const handleAuthRedirect = async () => {
            if (authInitialized) {
                const redirectResult = await handleRedirectResult();
                if (redirectResult.success && redirectResult.result) {
                    console.log('Redirect authentication successful');
                    navigate(`/main/${redirectResult.result.user.uid}`);
                }
            }
        };

        handleAuthRedirect();
    }, [authInitialized, navigate]);

    // Don't auto-navigate existing users - let them stay on quiz page

    const handleAnswerSelect = (questionIndex, answerIndex) => {
        const currentQuestion = shuffledQuestions[questionIndex];
        if (!currentQuestion) return;

        setSelectedAnswers(prev => ({
            ...prev,
            [questionIndex]: answerIndex
        }));

        // Check if answer is correct using the shuffled correct answer index
        const isCorrect = answerIndex === currentQuestion.correct_answer;
        
        setAnswerFeedback(prev => ({
            ...prev,
            [questionIndex]: {
                selected: answerIndex,
                correct: currentQuestion.correct_answer,
                isCorrect: isCorrect
            }
        }));

        // Update score
        if (isCorrect) {
            setScore(prev => prev + 1);
        }
    };

    const handleShowExplanation = (questionIndex) => {
        setShowExplanations(prev => ({
            ...prev,
            [questionIndex]: true
        }));
    };

    // Save quiz attempt to Supabase
    const saveQuizAttempt = async () => {
        if (attemptSaved || !user || !quizData) return;
        
        try {
            // Get Supabase user ID
            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
            if (!supabaseUser) {
                console.log('User not found in Supabase, skipping attempt save');
                return;
            }

            // Calculate time spent
            const timeSpentSeconds = quizStartTime ? 
                Math.floor((Date.now() - quizStartTime) / 1000) : 0;

            // Prepare question answers data
            const questionAnswers = shuffledQuestions.map((question, index) => ({
                question_id: question.id,
                question_text: question.question,
                selected_answer: selectedAnswers[index],
                correct_answer: question.correct_answer,
                is_correct: selectedAnswers[index] === question.correct_answer,
                options: question.options
            }));

            // Get attempt number (count previous attempts for this quiz)
            const previousAttempts = await database.getQuizAttempts(supabaseUser.id, quizData.document_name);
            const attemptNumber = previousAttempts.filter(attempt => 
                attempt.quiz_id === quizId
            ).length + 1;

            const attemptData = {
                quizId: quizId,
                documentName: quizData.document_name,
                score: score,
                totalQuestions: shuffledQuestions.length,
                timeSpentSeconds: timeSpentSeconds,
                attemptNumber: attemptNumber,
                questionAnswers: questionAnswers
            };

            console.log('💾 Saving quiz attempt:', attemptData);
            await database.saveQuizAttempt(supabaseUser.id, attemptData);
            console.log('✅ Quiz attempt saved successfully');
            setAttemptSaved(true);
            
            // Trigger analytics update
            try {
                await triggerQuizAnalyticsUpdate(supabaseUser.id, quizData.document_name, attemptData);
                console.log('✅ Analytics updated after quiz completion');
            } catch (analyticsError) {
                console.error('❌ Analytics update failed:', analyticsError);
                // Don't break the quiz flow if analytics fails
            }
        } catch (error) {
            console.error('❌ Failed to save quiz attempt:', error);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < shuffledQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            // Quiz completed
            setQuizCompleted(true);
            // Save attempt data
            saveQuizAttempt();
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const getGradeMessage = (score, total) => {
        const percentage = (score / total) * 100;
        
        if (percentage >= 90) {
            return { grade: 'A+', message: 'Excellent work! You have a strong understanding of this material.', emoji: '🎉' };
        } else if (percentage >= 80) {
            return { grade: 'A', message: 'Great job! You have a good grasp of the concepts.', emoji: '👏' };
        } else if (percentage >= 70) {
            return { grade: 'B', message: 'Good work! Consider reviewing some areas for improvement.', emoji: '👍' };
        } else if (percentage >= 60) {
            return { grade: 'C', message: 'Not bad! You might want to study this material more thoroughly.', emoji: '📚' };
        } else {
            return { grade: 'D', message: 'Keep studying! This material needs more attention.', emoji: '💪' };
        }
    };

    const handleRetakeQuiz = () => {
        setCurrentQuestionIndex(0);
        setSelectedAnswers({});
        setAnswerFeedback({});
        setShowExplanations({});
        setQuizCompleted(false);
        setScore(0);
        
        // Re-shuffle questions for retake
        if (quizData && (quizData.questions || quizData.quiz_data)) {
            const processedData = processQuizData(quizData);
            setShuffledQuestions(processedData.questions);
            // Reset start time for retake
            setQuizStartTime(Date.now());
            setAttemptSaved(false);
        }
    };

    const handleSignInWithGoogle = async () => {
        setAuthLoading(true);
        setAuthError(null);
        
        try {
            const result = await signInWithGoogle();
            
            if (result.method === 'popup' && result.result) {
                // For popup method, navigate immediately
                navigate(`/main/${result.result.user.uid}`);
                setShowSignUpModal(false);
            }
            // For redirect method, navigation will happen automatically
        } catch (error) {
            console.error('Authentication error:', error);
            setAuthError(error.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const saveQuizScore = async () => {
        if (!user) return;
        
        try {
            // Save score to Firebase
            const scoreData = {
                quizId: quizId,
                userId: user.uid,
                score: score,
                totalQuestions: shuffledQuestions.length,
                percentage: (score / shuffledQuestions.length) * 100,
                completedAt: new Date().toISOString(),
                answers: selectedAnswers
            };
            
            // TODO: Implement Firebase save logic
            console.log('Saving quiz score:', scoreData);
            
            // For now, just show success message
            alert('Quiz score saved successfully!');
        } catch (error) {
            console.error('Error saving quiz score:', error);
            alert('Failed to save quiz score. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="take-quiz-container">
                <div className="take-quiz-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading quiz...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="take-quiz-container">
                <div className="take-quiz-error">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate('/')} className="take-quiz-btn primary">
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    if (!quizData) {
        return (
            <div className="take-quiz-container">
                <div className="take-quiz-error">
                    <h2>Quiz Not Found</h2>
                    <p>The quiz you're looking for doesn't exist or has been removed.</p>
                    <button onClick={() => navigate('/')} className="take-quiz-btn primary">
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    const currentQuestion = shuffledQuestions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === shuffledQuestions.length - 1;
    const hasAnswered = selectedAnswers[currentQuestionIndex] !== undefined;
    const currentFeedback = answerFeedback[currentQuestionIndex];
    const showExplanation = showExplanations[currentQuestionIndex];

    return (
        <div className="take-quiz-container">
            {/* Header */}
            <div className="take-quiz-header">
                <div className="take-quiz-header-left">
                    <Link to={`/main/${uid}`} className="take-quiz-logo">
                        <div className="take-quiz-logo-icon"><img src="https://iili.io/KYpg7rg.png" alt="Study-Space Logo" className='take-quiz-logo-icon-img' /></div>
                        StudySpace
                    </Link>
                </div>
                <div className="take-quiz-header-right">
                    {/* <button className="take-quiz-header-btn secondary">
                        <ArrowLeft className="take-quiz-btn-icon" />
                        See all Notes
                    </button>
                    <button className="take-quiz-header-btn primary" onClick={() => setShowSignUpModal(true)}>
                        <Plus className="take-quiz-btn-icon" />
                        Get Started
                    </button> */}

                    <div style={{ position: 'relative' }}>
                        <Ellipsis size={40} onClick={handleShowTip} className='take-quiz-header-btn-icon take-quiz-ellipsis-btn' />
                        
                        {showTip && (
                            <div className="take-quiz-tip">
                                <div onClick={() => handleShareQuiz(`${window.location.origin}/shared-quiz/${uid}/${quizId}`)} className="take-quiz-tip-btn">
                                    <p>{shareClicked ? 'Copied!' : 'Share'}</p>
                                </div>
                                {/* <div onClick={handleDeleteQuiz} className="take-quiz-tip-btn">
                                    <p>Delete</p>
                                </div> */}
                            </div>
                        )}


                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="take-quiz-main">
                {!quizCompleted ? (
                    <div className="take-quiz-content">
                        {/* Quiz Questions - Full Width */}
                        <div className="take-quiz-questions-column">
                            <div className="take-quiz-questions-card">
                                <div className="take-quiz-questions-header">
                                    <div className="take-quiz-questions-title">
                                        <FileText className="take-quiz-title-icon" />
                                        Quiz questions
                                    </div>
                                    <div className="take-quiz-question-progress">
                                        {/* <Star className="take-quiz-star-icon" /> */}
                                        Question {currentQuestionIndex + 1} of {shuffledQuestions.length}
                                    </div>
                                </div>

                                <div className="take-quiz-question-container">
                                    <div className="take-quiz-question-header">
                                        <div className="take-quiz-question-label">
                                            <Star className="take-quiz-question-star" />
                                        </div>
                                        <div className="take-quiz-question-text">
                                            {currentQuestion.question}
                                        </div>
                                    </div>

                                    <div className="take-quiz-answer-options">
                                        {currentQuestion.options.map((option, index) => {
                                            const isSelected = selectedAnswers[currentQuestionIndex] === index;
                                            const isCorrect = index === currentQuestion.correct_answer;
                                            const showFeedback = hasAnswered;
                                            
                                            // Determine styling based on feedback
                                            let optionClass = 'take-quiz-answer-option';
                                            if (showFeedback) {
                                                if (isCorrect) {
                                                    optionClass += ' take-quiz-correct-answer';
                                                } else if (isSelected && !isCorrect) {
                                                    optionClass += ' take-quiz-incorrect-answer';
                                                } else {
                                                    optionClass += ' take-quiz-unselected';
                                                }
                                            } else if (isSelected) {
                                                optionClass += ' take-quiz-selected';
                                            } else {
                                                optionClass += ' take-quiz-unselected';
                                            }
                                            
                                            return (
                                                <div
                                                    key={index}
                                                    className={optionClass}
                                                    onClick={() => !showFeedback && handleAnswerSelect(currentQuestionIndex, index)}
                                                >
                                                    <div className="take-quiz-option-letter">
                                                        {String.fromCharCode(65 + index)}
                                                    </div>
                                                    <div className="take-quiz-option-text">
                                                        {option}
                                                    </div>
                                                    {showFeedback && (
                                                        <div className="take-quiz-option-icon">
                                                            {isCorrect ? <Check className="take-quiz-correct-icon" /> : 
                                                             (isSelected ? <X className="take-quiz-incorrect-icon" /> : null)}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Answer Feedback and Explanation Section */}
                                    {hasAnswered && currentFeedback && (
                                        <div className="take-quiz-feedback-section">
                                            <div className="take-quiz-feedback-message">
                                                {currentFeedback.isCorrect ? (
                                                    <div className="take-quiz-correct-feedback">
                                                        <Check className="take-quiz-feedback-icon correct" />
                                                        <span>Correct!</span>
                                                    </div>
                                                ) : (
                                                    <div className="take-quiz-incorrect-feedback">
                                                        <X className="take-quiz-feedback-icon incorrect" />
                                                        <span>Incorrect. The correct answer is {String.fromCharCode(65 + currentFeedback.correct)}.</span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Show Explanation Button */}
                                            {!showExplanation && (
                                                <button 
                                                    className="take-quiz-explanation-btn"
                                                    onClick={() => handleShowExplanation(currentQuestionIndex)}
                                                >
                                                    Show Explanation
                                                </button>
                                            )}
                                            
                                            {/* Explanation Content */}
                                            {showExplanation && currentQuestion.explanation && (
                                                <div className="take-quiz-explanation-content">
                                                    <div className="take-quiz-explanation-header">
                                                        <span>Explanation:</span>
                                                    </div>
                                                    <div className="take-quiz-explanation-text">
                                                        {currentQuestion.explanation}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="take-quiz-question-navigation">
                                        <button 
                                            className="take-quiz-nav-btn secondary"
                                            onClick={handlePrevQuestion}
                                            disabled={currentQuestionIndex === 0}
                                        >
                                            Previous
                                        </button>
                                        <button 
                                            className="take-quiz-nav-btn primary"
                                            onClick={handleNextQuestion}
                                            disabled={!hasAnswered}
                                        >
                                            {isLastQuestion ? 'Finish Quiz' : 'Next'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Quiz Completion Screen */
                    <div className="take-quiz-completion">
                        <div className="take-quiz-completion-card">
                            <div className="take-quiz-completion-header">
                                <Sparkles className="take-quiz-completion-icon" />
                                <h2>Quiz Completed!</h2>
                            </div>

                            <div className="take-quiz-score-display">
                                <div className="take-quiz-score-circle">
                                    <div className="take-quiz-score-number">
                                        {score}
                                    </div>
                                    <div className="take-quiz-score-total">
                                        / {shuffledQuestions.length}
                                    </div>
                                </div>
                                <div className="take-quiz-score-percentage">
                                    {Math.round((score / shuffledQuestions.length) * 100)}%
                                </div>
                            </div>

                            {(() => {
                                const gradeInfo = getGradeMessage(score, shuffledQuestions.length);
                                return (
                                    <div className="take-quiz-grade-message">
                                        <div className="take-quiz-grade-emoji">{gradeInfo.emoji}</div>
                                        <h3>Grade: {gradeInfo.grade}</h3>
                                        <p>{gradeInfo.message}</p>
                                    </div>
                                );
                            })()}

                            <div className="take-quiz-completion-actions">
                                {user ? (
                                    <>
                                        <button 
                                            className="take-quiz-action-btn primary"
                                            onClick={saveQuizScore}
                                        >
                                            Save Score
                                        </button>
                                        <button 
                                            className="take-quiz-action-btn secondary"
                                            onClick={handleRetakeQuiz}
                                        >
                                            Retake Quiz
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            className="take-quiz-action-btn primary"
                                            onClick={() => setShowSignUpModal(true)}
                                        >
                                            Sign In to Save Score
                                        </button>
                                        <button 
                                            className="take-quiz-action-btn secondary"
                                            onClick={handleRetakeQuiz}
                                        >
                                            Retake Quiz
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Sign Up Modal */}
            {showSignUpModal && (
                <div className="take-quiz-modal" onClick={() => setShowSignUpModal(false)}>
                    <div className="take-quiz-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="take-quiz-modal-close"
                            onClick={() => setShowSignUpModal(false)}
                        >
                            ×
                        </button>
                        <h2 className="take-quiz-modal-title">Welcome to StudySpace</h2>
                        <p className="take-quiz-modal-description">
                            Sign in to save your quiz scores and enjoy the full platform experience.
                        </p>
                        <button 
                            className="take-quiz-google-btn"
                            onClick={handleSignInWithGoogle}
                            disabled={authLoading}
                        >
                            <div className="take-quiz-google-logo">G</div>
                            {authLoading ? 'Signing in...' : 'Continue with Google'}
                        </button>
                        
                        {authError && (
                            <div className="auth-error">
                                {authError}
                            </div>
                        )}
                        <div className="take-quiz-separator">
                            <span>or</span>
                        </div>
                        <div className="take-quiz-email-section">
                            <input 
                                type="email" 
                                placeholder="Enter your email"
                                className="take-quiz-email-input"
                            />
                            <button className="take-quiz-email-btn">Get Started</button>
                        </div>
                        <p className="take-quiz-login-link">
                            Already have an account? <a href="#" onClick={() => setShowSignUpModal(true)}>Log in</a>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TakeQuiz;